import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Box } from '@mui/material'
import request from 'api'
import Detail from 'pages/notices/views/detail'
import List from 'pages/notices/views/list'
import { countryCode } from 'utils/i18n'

import i18n from '../../utils/i18n'

type Notice = {
  id: string
  title: string
  uri: string
  description: string
  published: string
}

export default function Notices() {
  const navigate = useNavigate()
  const location = useLocation()
  const { noticeId } = useParams()

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [newNoticeList, setNewNoticeList] = useState<Notice[]>([])
  const [oldNoticeList, setOldNoticeList] = useState<Notice[]>([])
  const [newNoticeDetail, setNewNoticeDetail] = useState<Notice>({} as Notice)

  const [showOldButton, setShowOldButton] = useState<boolean>(false)
  const [showNewButton, setShowNewButton] = useState<boolean>(false)

  const [isNewNoticeCompleted, setIsNewNoticeCompleted] = useState<boolean>(false)
  const [isOldNoticeCompleted, setIsOldNoticeCompleted] = useState<boolean>(false)

  const [noticeLastItem, setNoticeLastItem] = useState<HTMLElement | null>(null)

  const isDetail = noticeId !== undefined
  const currentVersion = location.pathname.includes('legacies') ? 'OLD' : 'NEW'
  const noticeList = currentVersion === 'NEW' ? newNoticeList : oldNoticeList
  const source = currentVersion === 'NEW' ? 'consent' : 'legacy'

  const internalState = useRef({
    offset: 0
  })

  const getDescription = (notice: Notice) => {
    const LEGACY_HOST = 'https://account.samsung.com'
    setIsLoading(true)
    return request
      .get(notice.uri)
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentBody')
        }

        const document = new DOMParser().parseFromString(res.data, 'text/html')

        const language = i18n.language?.replace('-', '_')
        if (language) {
          document.querySelectorAll('a').forEach((tag) => {
            if (tag.href.includes(`${LEGACY_HOST}`)) {
              tag.href += `?paramLocale=${language}`
            }
          })
        }

        const body = document.getElementsByTagName('body').item(0)?.innerHTML
        if (body !== undefined) {
          notice.description = body.replace(
            'body { word-wrap:break-word; word-break:keep-all; padding:1.5em; }',
            'body { word-wrap:break-word; word-break:keep-all; }'
          )
          setNewNoticeDetail(notice)
        }
      })
      .catch(() => {
        notice.description = '<div>Failed to load</div>'
        setNewNoticeDetail(notice)
      })
      .finally(() => setIsLoading(false))
  }

  const getNewNoticeApi = (offset: number) => {
    const appKey = process.env.REACT_APP_CONSENT_APP_KEY

    return request
      .get(`/notices/${source}`, {
        params: {
          noticeClientId: appKey,
          offset,
          countryCode: countryCode.toUpperCase(),
          language: i18n.language
        }
      })
      .then((response) => {
        if (response.data?.length === 0) {
          setIsNewNoticeCompleted(true)
          setShowOldButton(true)
        } else if (response.status !== 200) {
          throw new Error('Failed getNewNoticeApi')
        }
        const prevNoticeList = newNoticeList
        prevNoticeList.push(...response.data)
        setNewNoticeList(prevNoticeList)
      })
      .catch((error) => {
        setNewNoticeList([])
        setShowOldButton(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const getNewSingleNoticeApi = (id: string) => {
    const appKey = process.env.REACT_APP_CONSENT_APP_KEY

    return request
      .get(`/notices/${source}/${id}`, {
        params: {
          noticeClientId: appKey,
          countryCode: countryCode.toUpperCase(),
          language: i18n.language
        }
      })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Failed getNewNoticeApi')
        }
        getDescription(response.data as Notice)
      })
      .catch(() => {
        navigate(`/error/404?url=${location.pathname}`, { replace: true })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const getOldNoticeApi = () => {
    return request
      .get(`/notices/${source}`, {
        params: {
          countryCode: countryCode.toUpperCase()
        }
      })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Failed getOldNoticeApi')
        }

        setOldNoticeList(
          response.data.map((notice) => {
            notice.description = notice.description.replaceAll('&lt', '<').replaceAll('&gt', '>').replaceAll(/\n/g, '<br/>')
            return notice
          })
        )
      })
      .catch(() => {
        setOldNoticeList([])
        setShowNewButton(true)
      })
      .finally(() => {
        setIsLoading(false)
        setShowNewButton(true)
        setIsOldNoticeCompleted(true)
      })
  }

  const getNotice = (version: 'NEW' | 'OLD') => {
    switch (version) {
      case 'NEW':
        if (noticeId) {
          const selectedNotice = noticeList.filter((item) => item.id.toString() === noticeId)

          if (selectedNotice.length === 0) {
            getNewSingleNoticeApi(noticeId)
          } else {
            getDescription(selectedNotice[0])
          }
        } else if (isNewNoticeCompleted) {
          setIsLoading(false)
          setShowOldButton(true)
        } else if (newNoticeList.length === 0) {
          getNewNoticeApi(internalState.current.offset)
        }
        break
      case 'OLD':
        if (isOldNoticeCompleted) {
          setIsLoading(false)
          setShowNewButton(true)
        } else {
          getOldNoticeApi()
        }
        break
    }
  }

  useEffect(() => {
    setIsLoading(true)
    getNotice(currentVersion)
  }, [currentVersion, noticeId])

  useEffect(() => {
    let observerHandler: IntersectionObserver

    if (noticeLastItem) {
      observerHandler = new IntersectionObserver(
        ([entry]: IntersectionObserverEntry[], observer: IntersectionObserver) => {
          if (entry.isIntersecting && !isLoading && !isNewNoticeCompleted) {
            observer.unobserve(entry.target)
            setIsLoading(true)
            internalState.current.offset += 10
            getNewNoticeApi(internalState.current.offset)
          }
        },
        { threshold: 0.1 }
      )
      observerHandler.observe(noticeLastItem)
    }

    return () => observerHandler && observerHandler.disconnect()
  }, [noticeLastItem])

  const handleClickItem = (id: string) => {
    if (id === 'new') {
      setIsLoading(true)
      setShowNewButton(false)
      navigate('/notices')
    } else if (id === 'old') {
      setIsLoading(true)
      setShowOldButton(false)
      navigate('/notices/legacies')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      navigate(`./${id}`)
    }
  }

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {isDetail ? (
        <Detail
          isLoading={isLoading}
          currentVersion={currentVersion}
          notice={
            (currentVersion === 'NEW' && newNoticeDetail?.id?.toString() === noticeId && newNoticeDetail) ||
            noticeList.filter((notice) => notice.id.toString() === noticeId).at(0) ||
            ({} as Notice)
          }
        />
      ) : (
        <List
          isLoading={isLoading}
          currentVersion={currentVersion}
          noticeList={noticeList}
          setNoticeLastItem={setNoticeLastItem}
          handleClickItem={handleClickItem}
          showOldButton={showOldButton}
          showNewButton={showNewButton}
        />
      )}
    </Box>
  )
}
